import { t } from "i18next";
import { DOCUMENT_IDS } from "./documentIDs";
import { JobCandidateFormSource } from "./enums";
import { CANDIDATE_APPLY_PAGE_PATH } from "./Paths";

export const QUESTIONAIRE_PAGE = "questionaire";
export const APPLICATION_SUMMERY_PAGE = "application-summary";

export const PRIMARY_APPLICATION_SUMMERY_PAGE = "first-step-summary";
export const SECONDARY_APPLICATION_SUMMERY_PAGE = "final-step-summary";

export const BANKID_PAGE = "bank-id-auth";
export const UPLOAD_CV_PAGE = "upload-cv";
export const SCHEDULE_INTERVIEW = "schedule-interview";
export const ADD_REFERENCE_PAGE = "add-references";
export const SELECT_SYSTEM_KNOWLEDGE_PAGE = "system-knowledge";
export const UPLOAD_ID_CARD_PAGE = "upload-id-card";
export const UPLOAD_CPR_CERT_PAGE = "upload-cpr-certificate";
export const UPLOAD_CRIMINAL_RECORD_PAGE = "criminal-record-register";

export const APPLICATION_SECTIONS = ["basic", "primary", "secondary"];

export const getJobApplicationProcessSteps = ({
  searchParams,
  candidateData,
  profile,
}) => {
  const previewData = searchParams.get("preview");
  const source = parseInt(
    `${searchParams.get("source") ?? JobCandidateFormSource.external}`,
  );

  const getStepPath = (step) => {
    return (
      CANDIDATE_APPLY_PAGE_PATH +
      `${previewData ? `?preview=${previewData}` : ""}&page=${step}&source=${source}`
    );
  };

  const uploadDocumentTypes = candidateData?.documentTypes ?? [];
  const uploadedDocuments = Object.values(candidateData?.documents ?? {});
  const hasUploadedDocument = (documentType) =>
    uploadDocumentTypes.includes(documentType) ||
    uploadedDocuments.some((document) => document?.type === documentType);

  const hasBecomeCandidate =
    candidateData?.id !== undefined && candidateData?.id !== "";

  const hasAnsweredQuiz =
    hasBecomeCandidate &&
    (candidateData["questionaireData"] === null ||
      candidateData["questionaireData"]);
  const candidateUid =
    candidateData?.candidate?.uid ?? candidateData?.uid ?? "external";

  const hasBankId =
    hasBecomeCandidate &&
    (candidateData?.candidate?.credentials?.bankIdVerified ||
      candidateUid !== "external" ||
      profile?.bankIdVerified === true);

  const completedAllBasicSteps = hasBecomeCandidate && hasBankId;

  const hasUploadedCV =
    completedAllBasicSteps && hasUploadedDocument(DOCUMENT_IDS.CV);

  const hasAddedReferences =
    completedAllBasicSteps && (candidateData?.referencesCount ?? 0) > 0;

  const candidateSystemKnowledge =
    candidateData?.candidate?.credentials?.systemKnowledge ??
    candidateData?.systemKnowledge ??
    [];
  const hasAddedSystemKnowledge =
    completedAllBasicSteps && candidateSystemKnowledge.length > 0;
  const hasScheduletdInterview =
    completedAllBasicSteps &&
    (candidateData?.eventBookingId ?? candidateData?.hasInterviewBooking);

  const completedAllPrimarySteps =
    hasUploadedCV &&
    hasAddedReferences &&
    hasAddedSystemKnowledge &&
    hasScheduletdInterview;

  const hasUploadIDCard =
    completedAllBasicSteps &&
    hasUploadedDocument(DOCUMENT_IDS.PASSPORT);
  const hasUploadCPR =
    completedAllBasicSteps &&
    hasUploadedDocument(DOCUMENT_IDS.CPR_CERTIFICATE);
  const hasUploadCriminalRecord =
    completedAllBasicSteps &&
    hasUploadedDocument(DOCUMENT_IDS.CRIMINAL_RECORD);

  const completedAllSecondarySteps =
    completedAllBasicSteps &&
    completedAllPrimarySteps &&
    hasUploadIDCard &&
    hasUploadCPR &&
    hasUploadCriminalRecord;

  return [
    /// BASIC STEPS
    {
      id: QUESTIONAIRE_PAGE,
      title: t("candidatedetail.stepper.questionaire.title"),
      description: t("candidatedetail.stepper.questionaire.description"),
      icon: "question circle outline",
      completed: hasAnsweredQuiz,
      type: "basic",
      path: getStepPath(QUESTIONAIRE_PAGE),
      authRequired: false,
      priority: "medium",
    },

    {
      id: APPLICATION_SUMMERY_PAGE,
      title: t("candidatedetail.stepper.application_summery.title"),
      description: t("candidatedetail.stepper.application_summery.description"),
      icon: "file alternate outline",
      completed: completedAllBasicSteps,
      type: "basic",
      path: getStepPath(APPLICATION_SUMMERY_PAGE),
      authRequired: false,
    },

    /// PRIMARY STEPS
    {
      id: BANKID_PAGE,
      title: t("candidatedetail.stepper.verified.title"),
      description: t(
        `candidatedetail.stepper.${hasBankId ? "bankid_verified" : "bankid"}.description`,
      ),
      icon: "id badge outline",
      completed: hasBankId,
      type: "primary",
      path: getStepPath(BANKID_PAGE),
      removeFromNavigation: true,
      authRequired: false,
      priority: "high",
    },

    {
      id: UPLOAD_CV_PAGE,
      title: t("candidatedetail.stepper.upload_cv.title"),
      description: t("candidatedetail.stepper.upload_cv.description"),
      icon: "briefcase",
      completed: hasUploadedCV,
      type: "primary",
      path: getStepPath(UPLOAD_CV_PAGE),
      authRequired: true,
      priority: "high",
    },
    {
      id: ADD_REFERENCE_PAGE,

      title: t("candidatedetail.stepper.add_references.title"),
      description: t("candidatedetail.stepper.add_references.description"),

      icon: "briefcase",
      type: "primary",
      completed: hasAddedReferences,
      path: getStepPath(ADD_REFERENCE_PAGE),
      authRequired: true,
      priority: "high",
    },

    {
      id: SELECT_SYSTEM_KNOWLEDGE_PAGE,

      title: t("candidatedetail.stepper.select_system_knowledge.title"),
      description: t(
        "candidatedetail.stepper.select_system_knowledge.description",
      ),

      icon: "computer",
      type: "primary",
      completed: hasAddedSystemKnowledge,
      path: getStepPath(SELECT_SYSTEM_KNOWLEDGE_PAGE),
      authRequired: true,
      priority: "medium",
    },

    {
      id: SCHEDULE_INTERVIEW,
      asset: "interview",
      title: t("candidatedetail.stepper.schedule_interview.title"),
      description: t("candidatedetail.stepper.schedule_interview.description"),

      icon: "calendar check outline",
      completed: hasScheduletdInterview,
      type: "primary",
      path: getStepPath(SCHEDULE_INTERVIEW),
      authRequired: true,
      priority: "low",
    },

    {
      id: PRIMARY_APPLICATION_SUMMERY_PAGE,

      title: t("candidatedetail.stepper.primary_application_summery.title"),
      description: t(
        "candidatedetail.stepper.primary_application_summery.description",
      ),

      icon: "file alternate outline",
      completed: completedAllPrimarySteps,
      type: "primary",
      path: getStepPath(PRIMARY_APPLICATION_SUMMERY_PAGE),
      authRequired: true,
    },

    /// SECONDAIRY STEPS
    {
      id: UPLOAD_ID_CARD_PAGE,

      title: t("candidatedetail.stepper.upload_id_card.title"),
      description: t("candidatedetail.stepper.upload_id_card.description"),

      icon: "id card outline",
      completed: hasUploadIDCard,
      type: "secondary",
      path: getStepPath(UPLOAD_ID_CARD_PAGE),
      authRequired: true,
      priority: "medium",
    },
    {
      id: UPLOAD_CPR_CERT_PAGE,

      title: t("candidatedetail.stepper.upload_cpr_certificate.title"),
      description: t(
        "candidatedetail.stepper.upload_cpr_certificate.description",
      ),

      type: "secondary",
      completed: hasUploadCPR,
      icon: "certificate",
      path: getStepPath(UPLOAD_CPR_CERT_PAGE),
      authRequired: true,
      priority: "medium",
    },

    {
      id: UPLOAD_CRIMINAL_RECORD_PAGE,

      title: t("candidatedetail.stepper.upload_criminal_record.title"),
      description: t(
        "candidatedetail.stepper.upload_criminal_record.description",
      ),

      type: "secondary",
      icon: "user secret",
      completed: hasUploadCriminalRecord,
      path: getStepPath(UPLOAD_CRIMINAL_RECORD_PAGE),
      authRequired: true,
      priority: "low",
    },
    {
      id: SECONDARY_APPLICATION_SUMMERY_PAGE,

      title: t("candidatedetail.stepper.secondary_application_summery.title"),
      description: t(
        "candidatedetail.stepper.secondary_application_summery.description",
      ),

      icon: "file alternate outline",
      completed: completedAllSecondarySteps,
      type: "secondary",
      path: getStepPath(SECONDARY_APPLICATION_SUMMERY_PAGE),
      authRequired: true,
    },
  ];
};

export const getNextIncompleteApplicationStep = (steps = []) =>
  steps.find((page) => !page.completed) ?? null;

export const getApplicationContinuationStep = (steps = []) =>
  getNextIncompleteApplicationStep(steps) ?? steps[steps.length - 1] ?? null;

export const getSectionContent = (type) => {
  return {
    title: t(`candidatedetail.section.${type}.title`),
    description: t(`candidatedetail.section.${type}.description`),
  };
};

export const calculateCandidateLikelihoodPercentage = ({ steps }) => {
  const newSteps = steps.filter((step) => {
    return ![
      APPLICATION_SUMMERY_PAGE,
      PRIMARY_APPLICATION_SUMMERY_PAGE,
      SECONDARY_APPLICATION_SUMMERY_PAGE,
    ].includes(step.id);
  });

  const BASE_POSSIBILITY = 100;

  if (newSteps.length === 0) {
    return {
      percentage: 0,
      stepWeights: [],
    };
  }

  const priorityWeights = {
    high: 50, // High priority steps get 50% of total weight
    medium: 30, // Medium priority steps get 30% of total weight
    low: 20, // Low priority steps get 20% of total weight
  };

  // Group steps by priority
  const stepsByPriority = {
    high: newSteps.filter((step) => step.priority === "high"),
    medium: newSteps.filter((step) => step.priority === "medium"),
    low: newSteps.filter((step) => step.priority === "low"),
  };

  // Calculate total weight for each priority level
  const totalWeights = {
    high: stepsByPriority.high.length * priorityWeights.high,
    medium: stepsByPriority.medium.length * priorityWeights.medium,
    low: stepsByPriority.low.length * priorityWeights.low,
  };

  const totalPossibleWeight =
    totalWeights.high + totalWeights.medium + totalWeights.low;

  // Calculate points per step for each priority level
  let pointsPerStep = {};
  let stepWeights = [];

  // If no steps have priority or total weight is 0, distribute equally
  if (totalPossibleWeight === 0) {
    const equalWeight = BASE_POSSIBILITY / newSteps.length;
    pointsPerStep = { equal: equalWeight };

    stepWeights = newSteps.map((step) => ({
      stepId: step.id,
      title: step.title,
      priority: step.priority || "none",
      weight: parseFloat(equalWeight.toFixed(2)),
      completed: step.completed,
      pointsEarned: step.completed ? parseFloat(equalWeight.toFixed(2)) : 0,
    }));

    const completedSteps = newSteps.filter((step) => step.completed).length;
    const percentage = Math.round(
      (completedSteps / newSteps.length) * BASE_POSSIBILITY,
    );

    return {
      percentage,
      stepWeights,
    };
  }

  // Calculate points per step for each priority level
  pointsPerStep = {
    high:
      totalWeights.high > 0
        ? (priorityWeights.high * BASE_POSSIBILITY) /
          (100 * stepsByPriority.high.length)
        : 0,
    medium:
      totalWeights.medium > 0
        ? (priorityWeights.medium * BASE_POSSIBILITY) /
          (100 * stepsByPriority.medium.length)
        : 0,
    low:
      totalWeights.low > 0
        ? (priorityWeights.low * BASE_POSSIBILITY) /
          (100 * stepsByPriority.low.length)
        : 0,
  };

  // Create step weights array with detailed information
  stepWeights = newSteps.map((step) => {
    const priority = step.priority || "none";
    const weight = pointsPerStep[priority] || 0;
    const pointsEarned = step.completed ? weight : 0;

    return {
      stepId: step.id,
      title: step.title,
      priority: priority,
      weight: parseFloat(weight.toFixed(2)),
      completed: step.completed,
      pointsEarned: parseFloat(pointsEarned.toFixed(2)),
    };
  });

  let chances_you_have = 0;

  // Calculate points for completed steps
  Object.keys(stepsByPriority).forEach((priority) => {
    const completedStepsInPriority = stepsByPriority[priority].filter(
      (step) => step.completed,
    );
    chances_you_have +=
      completedStepsInPriority.length * pointsPerStep[priority];
  });

  // Round to nearest integer and ensure it doesn't exceed 100
  const percentage = Math.min(Math.round(chances_you_have), BASE_POSSIBILITY);

  return {
    percentage,
    stepWeights,
  };
};

export const calculateProfileStrength = ({ profile }) => {
  const scoreMap = {
    bankIdVerified: 15,
    healthcareVerified: 15,
    email: 10,
    phoneNumber: 10,
    profession: 10,
    workRoles: 10,
    systemKnowledge: 10,
    workFields: 5,
    employmentTypes: 5,
    locations: 5,
    lookingForJob: 5,
  };

  let totalScore = 0;

  // Verification
  if (profile?.bankIdVerified) totalScore += scoreMap.bankIdVerified;
  if (profile?.healthcareVerified) totalScore += scoreMap.healthcareVerified;

  // Contact Info
  if (profile?.email?.length > 0) totalScore += scoreMap.email;
  if (profile?.phoneNumber?.length > 0) totalScore += scoreMap.phoneNumber;

  // Professional Info
  if (profile?.profession?.length > 0) totalScore += scoreMap.profession;
  if (profile?.homePreferences?.workRoles?.length > 0)
    totalScore += scoreMap.workRoles;
  if (profile?.homePreferences?.systemKnowledge?.length > 0)
    totalScore += scoreMap.systemKnowledge;
  if (profile?.homePreferences?.workFields?.length > 0)
    totalScore += scoreMap.workFields;

  // Preferences
  if (profile?.homePreferences?.employmentTypes?.length > 0)
    totalScore += scoreMap.employmentTypes;
  if (profile?.homePreferences?.locations?.length > 0)
    totalScore += scoreMap.locations;
  if (profile?.homePreferences?.lookingForJob)
    totalScore += scoreMap.lookingForJob;

  // Return percentage (0–100)
  return {
    totalScore,
    scoreMap,
  };
};
